import { cn } from "../utils/cn";
import type { ButtonProps } from "./Button";
import { Button } from "./Button";

// ── Types ─────────────────────────────────────────────────────────────────────

export type EmptyStateVariant =
  | "no-trips"
  | "no-results"
  | "no-bookings"
  | "no-reviews"
  | "offline"
  | "no-operators";

export type EmptyStateSize = "sm" | "md" | "lg";

export interface EmptyStateProps {
  variant?: EmptyStateVariant;
  title?: string;
  description?: string;
  action?: {
    label: string;
    onClick: () => void;
    variant?: ButtonProps["variant"];
  };
  size?: EmptyStateSize;
  className?: string;
}

// ── Illustrations ─────────────────────────────────────────────────────────────

function CompassIllustration({ size }: { size: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 120 120" fill="none" aria-hidden="true">
      <circle cx="60" cy="60" r="44" stroke="currentColor" strokeWidth="3" className="text-border-default" />
      <circle cx="60" cy="60" r="36" fill="currentColor" className="text-surface-raised" />
      {/* Compass rose */}
      <polygon points="60,28 64,56 60,52 56,56" fill="currentColor" className="text-red-400 dark:text-red-500" />
      <polygon points="60,92 56,64 60,68 64,64" fill="currentColor" className="text-content-tertiary" />
      <polygon points="92,60 64,56 68,60 64,64" fill="currentColor" className="text-content-tertiary" />
      <polygon points="28,60 56,64 52,60 56,56" fill="currentColor" className="text-content-tertiary" />
      <circle cx="60" cy="60" r="5" fill="currentColor" className="text-content-primary" />
      {/* N label */}
      <text x="57" y="24" fontSize="9" fontWeight="700" fill="currentColor" className="text-content-primary" fontFamily="sans-serif">N</text>
    </svg>
  );
}

function TentIllustration({ size }: { size: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 120 120" fill="none" aria-hidden="true">
      {/* Ground */}
      <line x1="10" y1="90" x2="110" y2="90" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" className="text-border-default" />
      {/* Tent body */}
      <path d="M20 90 L60 28 L100 90 Z" fill="currentColor" className="text-violet-200 dark:text-violet-900/60" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
      {/* Inner panel */}
      <path d="M42 90 L60 50 L78 90 Z" fill="currentColor" className="text-violet-400/60 dark:text-violet-700/60" />
      {/* Door opening */}
      <path d="M50 90 Q60 72 70 90" fill="currentColor" className="text-surface" />
      {/* Stars */}
      <circle cx="30" cy="20" r="1.5" fill="currentColor" className="text-yellow-400" />
      <circle cx="88" cy="14" r="2" fill="currentColor" className="text-yellow-400" />
      <circle cx="100" cy="32" r="1.5" fill="currentColor" className="text-yellow-300" />
      <circle cx="18" cy="42" r="1" fill="currentColor" className="text-yellow-400" />
    </svg>
  );
}

function MountainPathIllustration({ size }: { size: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 120 120" fill="none" aria-hidden="true">
      {/* Sky */}
      <rect width="120" height="120" rx="12" fill="currentColor" className="text-surface-raised" />
      {/* Far mountain */}
      <path d="M0 90 L35 40 L70 90 Z" fill="currentColor" className="text-slate-300 dark:text-slate-500" />
      {/* Near mountain */}
      <path d="M40 90 L80 30 L120 90 Z" fill="currentColor" className="text-slate-400 dark:text-slate-400" />
      {/* Snow cap */}
      <path d="M80 30 L88 50 L72 50 Z" fill="white" opacity="0.8" />
      {/* Path winding up */}
      <path d="M10 90 Q30 80 40 65 Q50 50 65 55 Q80 60 90 50" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeDasharray="5 4" className="text-yellow-500 dark:text-yellow-400" fill="none" />
      {/* Walker dot */}
      <circle cx="65" cy="55" r="4" fill="currentColor" className="text-brand-purple" />
    </svg>
  );
}

function StarMountainIllustration({ size }: { size: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 120 120" fill="none" aria-hidden="true">
      {/* Mountain silhouette */}
      <path d="M10 95 L50 35 L70 60 L85 42 L110 95 Z" fill="currentColor" className="text-slate-200 dark:text-slate-500" />
      {/* Star above mountain */}
      <path
        d="M60 8 L63.5 19 L75 19 L65.8 25.5 L69.3 36.5 L60 30 L50.7 36.5 L54.2 25.5 L45 19 L56.5 19 Z"
        fill="currentColor"
        className="text-yellow-400 dark:text-yellow-300"
      />
      {/* Small stars */}
      <path d="M20 18 L21.2 22 L25 22 L22 24.4 L23.2 28.4 L20 26 L16.8 28.4 L18 24.4 L15 22 L18.8 22 Z" fill="currentColor" className="text-yellow-300 dark:text-yellow-400" opacity="0.7" />
      <path d="M95 25 L96 28 L99 28 L96.8 29.8 L97.8 32.8 L95 31 L92.2 32.8 L93.2 29.8 L91 28 L94 28 Z" fill="currentColor" className="text-yellow-300 dark:text-yellow-400" opacity="0.6" />
      {/* Ground line */}
      <line x1="5" y1="95" x2="115" y2="95" stroke="currentColor" strokeWidth="2" className="text-border-default" />
    </svg>
  );
}

function PersonBackpackIllustration({ size }: { size: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 120 120" fill="none" aria-hidden="true">
      {/* Ground */}
      <line x1="5" y1="100" x2="115" y2="100" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" className="text-border-default" />
      {/* Mountain bg */}
      <path d="M0 100 L40 55 L80 100 Z" fill="currentColor" className="text-surface-raised" />
      <path d="M55 100 L85 45 L115 100 Z" fill="currentColor" className="text-surface-overlay" />
      {/* Person body */}
      <circle cx="60" cy="38" r="9" fill="currentColor" className="text-content-secondary" />
      <rect x="51" y="50" width="18" height="22" rx="4" fill="currentColor" className="text-content-secondary" />
      {/* Legs */}
      <line x1="55" y1="72" x2="52" y2="90" stroke="currentColor" strokeWidth="4" strokeLinecap="round" className="text-content-secondary" />
      <line x1="65" y1="72" x2="68" y2="90" stroke="currentColor" strokeWidth="4" strokeLinecap="round" className="text-content-secondary" />
      {/* Backpack */}
      <rect x="66" y="48" width="13" height="18" rx="3" fill="currentColor" className="text-brand-purple" />
      <line x1="66" y1="52" x2="69" y2="52" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" className="text-violet-300" />
      <line x1="66" y1="55" x2="69" y2="55" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" className="text-violet-300" />
      {/* Walking stick */}
      <line x1="48" y1="58" x2="42" y2="92" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" className="text-content-tertiary" />
    </svg>
  );
}

function BackpackIllustration({ size }: { size: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 120 120" fill="none" aria-hidden="true">
      {/* Backpack body */}
      <rect x="28" y="38" width="64" height="66" rx="12" fill="currentColor" className="text-violet-200 dark:text-violet-900/70" stroke="currentColor" strokeWidth="2" />
      {/* Top flap */}
      <rect x="32" y="26" width="56" height="20" rx="8" fill="currentColor" className="text-violet-300 dark:text-violet-800" stroke="currentColor" strokeWidth="2" />
      {/* Shoulder straps */}
      <path d="M40 38 Q30 55 32 80" stroke="currentColor" strokeWidth="5" strokeLinecap="round" className="text-violet-400 dark:text-violet-700" fill="none" />
      <path d="M80 38 Q90 55 88 80" stroke="currentColor" strokeWidth="5" strokeLinecap="round" className="text-violet-400 dark:text-violet-700" fill="none" />
      {/* Front pocket */}
      <rect x="38" y="70" width="44" height="26" rx="6" fill="currentColor" className="text-violet-300 dark:text-violet-800" stroke="currentColor" strokeWidth="1.5" />
      {/* Pocket zipper */}
      <line x1="50" y1="83" x2="70" y2="83" stroke="currentColor" strokeWidth="2" strokeLinecap="round" className="text-violet-500 dark:text-violet-600" />
      <circle cx="70" cy="83" r="2.5" fill="currentColor" className="text-violet-500 dark:text-violet-500" />
      {/* Main zipper */}
      <path d="M36 42 Q60 36 84 42" stroke="currentColor" strokeWidth="2" strokeLinecap="round" className="text-violet-400 dark:text-violet-600" fill="none" />
    </svg>
  );
}

// ── Config map ────────────────────────────────────────────────────────────────

const VARIANT_CONFIG: Record<
  EmptyStateVariant,
  { illustration: (size: number) => React.ReactNode; title: string; description: string }
> = {
  "no-trips": {
    illustration: (s) => <TentIllustration size={s} />,
    title: "No trips yet",
    description: "Browse available expeditions and book your first Himalayan adventure.",
  },
  "no-results": {
    illustration: (s) => <CompassIllustration size={s} />,
    title: "No results found",
    description: "Try adjusting your filters or search terms to find what you're looking for.",
  },
  "no-bookings": {
    illustration: (s) => <PersonBackpackIllustration size={s} />,
    title: "No bookings yet",
    description: "Once you book a trip, it will appear here. Start exploring!",
  },
  "no-reviews": {
    illustration: (s) => <StarMountainIllustration size={s} />,
    title: "No reviews yet",
    description: "Be the first to share your experience and help other adventurers.",
  },
  offline: {
    illustration: (s) => <MountainPathIllustration size={s} />,
    title: "You're off the beaten path",
    description: "Check your connection and try again when you're back in range.",
  },
  "no-operators": {
    illustration: (s) => <BackpackIllustration size={s} />,
    title: "No operators found",
    description: "No operators match your criteria for this route. Try widening your search.",
  },
};

const SIZE_CONFIG: Record<EmptyStateSize, { illustration: number; title: string; desc: string; gap: string }> = {
  sm: { illustration: 80, title: "text-sm font-semibold", desc: "text-xs", gap: "gap-3" },
  md: { illustration: 110, title: "text-base font-semibold", desc: "text-sm", gap: "gap-4" },
  lg: { illustration: 140, title: "text-lg font-semibold", desc: "text-sm", gap: "gap-5" },
};

// ── Component ─────────────────────────────────────────────────────────────────

export function EmptyState({
  variant = "no-results",
  title,
  description,
  action,
  size = "md",
  className,
}: EmptyStateProps) {
  const config = VARIANT_CONFIG[variant];
  const sizeConfig = SIZE_CONFIG[size];

  const displayTitle = title ?? config.title;
  const displayDesc = description ?? config.description;

  return (
    <div
      className={cn(
        "flex flex-col items-center justify-center text-center",
        sizeConfig.gap,
        className,
      )}
    >
      {config.illustration(sizeConfig.illustration)}

      <div className={cn("flex flex-col gap-1", size === "sm" ? "gap-0.5" : "gap-1.5")}>
        <p className={cn(sizeConfig.title, "text-content-primary")}>{displayTitle}</p>
        <p className={cn(sizeConfig.desc, "text-content-secondary max-w-xs")}>{displayDesc}</p>
      </div>

      {action && (
        <Button
          variant={action.variant ?? "primary"}
          size={size === "lg" ? "md" : "sm"}
          onClick={action.onClick}
        >
          {action.label}
        </Button>
      )}
    </div>
  );
}

EmptyState.displayName = "EmptyState";
